import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Copy, Users, Globe, Lock, LogOut, RefreshCw, Pencil, Link } from "lucide-react";
import { TippingChampionship } from "@/components/leagues/TippingChampionship";
import { getInviteUrl, getInviteDisplayUrl } from "@/lib/shareUtils";
import { analytics } from "@/lib/analytics";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmojiPicker } from "@/components/leagues/EmojiPicker";

const LeagueDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [leaving, setLeaving] = useState(false);
  const [refreshingCode, setRefreshingCode] = useState(false);
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editIsPublic, setEditIsPublic] = useState(false);
  const [editEmoji, setEditEmoji] = useState("🏆");
  const [saving, setSaving] = useState(false);

  const { data: league, isLoading: loadingLeague } = useQuery({
    queryKey: ["league", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leagues")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: members = [], isLoading: loadingMembers } = useQuery({
    queryKey: ["league-members", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_members")
        .select("id, user_id, role, joined_at, profiles(display_name, avatar_icon, team_colour, favourite_team)")
        .eq("league_id", id!);
      if (error) throw error;
      return data ?? [];
    },
  });

  const myMembership = members.find((m) => m.user_id === user?.id);
  const isOwner = myMembership?.role === "owner";

  const handleRefreshCode = async () => {
    setConfirmRefresh(false);
    setRefreshingCode(true);
    const { data: newCode, error: rpcError } = await supabase.rpc("generate_invite_code");
    if (rpcError || !newCode) {
      setRefreshingCode(false);
      toast({ title: "Failed to generate code", description: rpcError?.message, variant: "destructive" });
      return;
    }
    const { error } = await supabase.from("leagues").update({ invite_code: newCode }).eq("id", id!);
    setRefreshingCode(false);
    if (error) {
      toast({ title: "Failed to update code", description: error.message, variant: "destructive" });
    } else {
      queryClient.invalidateQueries({ queryKey: ["league", id] });
      toast({ title: "Invite code refreshed!" });
    }
  };


  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteLeague = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    const { error } = await supabase.rpc("delete_league", { p_league_id: id! });
    setDeleting(false);
    if (error) {
      toast({ title: "Failed to delete league", description: error.message, variant: "destructive" });
    } else {
      queryClient.invalidateQueries({ queryKey: ["my-leagues"] });
      toast({ title: "League deleted" });
      navigate("/leagues", { replace: true });
    }
  };

  const handleLeave = async () => {
    if (!myMembership || !user) return;
    setLeaving(true);
    const { error } = await supabase
      .from("league_members")
      .delete()
      .eq("id", myMembership.id);
    setLeaving(false);
    if (error) {
      toast({ title: "Failed to leave", description: error.message, variant: "destructive" });
    } else {
      analytics.track("league_left", { league_id: id });
      queryClient.invalidateQueries({ queryKey: ["my-leagues"] });
      toast({ title: "You left the league." });
      navigate("/leagues", { replace: true });
    }
  };

  if (loadingLeague) {
    return <div className="px-4 py-10 text-center text-muted-foreground">Loading…</div>;
  }

  if (!league) {
    return <div className="px-4 py-10 text-center text-muted-foreground">League not found.</div>;
  }

  return (
    <div className="px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Header */}
        <div>
          <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/leagues")}>
            ← Back to Leagues
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-4xl">{league.emoji || "🏆"}</span>
            <h1 className="text-2xl font-bold md:text-3xl">{league.name}</h1>
            {isOwner && (
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => {
                setEditName(league.name);
                setEditDesc(league.description || "");
                setEditIsPublic(league.is_public ?? false);
                setEditEmoji(league.emoji || "🏆");
                setEditOpen(true);
              }}>
                <Pencil className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
          </div>
          {league.description && (
            <p className="mt-1 text-muted-foreground">{league.description}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" /> {league.member_count ?? 0} members
            </span>
            <span className="flex items-center gap-1">
              {league.is_public ? <><Globe className="h-4 w-4" /> Public</> : <><Lock className="h-4 w-4" /> Private</>}
            </span>
          </div>
        </div>

        {/* Invite Code */}
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Invite Code</p>
              <p className="text-xl font-mono font-bold tracking-widest">{league.invite_code}</p>
            </div>
            <div className="flex items-center gap-2">
              {isOwner && (
                <Button
                  size="icon"
                  variant="outline"
                  disabled={refreshingCode}
                  onClick={() => setConfirmRefresh(true)}
                  title="Refresh invite code"
                >
                  <RefreshCw className={cn("h-4 w-4", refreshingCode && "animate-spin")} />
                </Button>
              )}
              <Button
                size="icon"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(league.invite_code);
                  analytics.track("invite_shared", { method: "copy_code" });
                  toast({ title: "Code copied!" });
                }}
                title="Copy invite code"
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(getInviteUrl(league.invite_code));
                  analytics.track("invite_shared", { method: "copy_link" });
                  toast({ title: "Link copied!" });
                }}
                title="Copy invite link"
              >
                <Link className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tipping Championship */}
        {id && <TippingChampionship leagueId={id} isOwner={isOwner} />}

        {/* Leave */}
        {myMembership && (
          <div className="space-y-3">
            {isOwner ? (
              <>
                <p className="text-center text-sm text-muted-foreground">Owners cannot leave their league.</p>
                <Button variant="outline" className="w-full text-destructive border-destructive/30 hover:bg-destructive/10" disabled={deleting} onClick={() => setConfirmDelete(true)}>
                  {deleting ? "Deleting…" : "Delete League"}
                </Button>
              </>
            ) : (
              <Button variant="destructive" className="w-full font-semibold" disabled={leaving} onClick={handleLeave}>
                <LogOut className="mr-2 h-4 w-4" />
                {leaving ? "Leaving…" : "Leave League"}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Refresh Code Confirmation */}
      <AlertDialog open={confirmRefresh} onOpenChange={setConfirmRefresh}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refresh invite code?</AlertDialogTitle>
            <AlertDialogDescription>
              This will invalidate the current invite code. Anyone with the old code won't be able to join. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRefreshCode}>Refresh</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete League Confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete League</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{league.name}</strong> and remove all members. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteLeague} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete League</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit League</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>League Name</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="e.g. The Pit Crew" maxLength={50} />
            </div>
            <div className="space-y-2">
              <Label>Emoji</Label>
              <EmojiPicker value={editEmoji} onChange={setEditEmoji} />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="What's your league about?" rows={3} />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="flex items-center gap-2">
                {editIsPublic ? <Globe className="h-4 w-4 text-muted-foreground" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                <span className="text-sm font-medium">{editIsPublic ? "Public" : "Private"}</span>
              </div>
              <Switch checked={editIsPublic} onCheckedChange={setEditIsPublic} />
            </div>
            <Button
              className="w-full font-semibold"
              disabled={!editName.trim() || saving}
              onClick={async () => {
                setSaving(true);
                const { error } = await supabase.from("leagues").update({
                  name: editName.trim(),
                  description: editDesc.trim() || null,
                  is_public: editIsPublic,
                  emoji: editEmoji,
                }).eq("id", id!);
                setSaving(false);
                if (error) {
                  toast({ title: "Failed to update league", description: error.message, variant: "destructive" });
                } else {
                  queryClient.invalidateQueries({ queryKey: ["league", id] });
                  queryClient.invalidateQueries({ queryKey: ["my-leagues"] });
                  toast({ title: "League updated!" });
                  setEditOpen(false);
                }
              }}
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeagueDetail;
